import { Component, HostListener, inject, OnInit, signal } from "@angular/core";
import { Router } from "@angular/router";
import { CommonModule } from "@angular/common";

import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { KENDO_TILELAYOUT } from "@progress/kendo-angular-layout";
import {
  SVGIcon,
  anchorIcon,
  cartIcon,
  codeIcon,
  slidersIcon,
  userIcon,
  userOutlineIcon,
  wrenchIcon,
} from "@progress/kendo-svg-icons";

import { menuyItems, MenuNode } from "../core/menu/menuItems";
import { BreadcrumbService } from "../core/services/breadcrumb.service";
import { PermissionsService } from "../app-permission.service";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  styleUrls: ["./main.component.css"],
  standalone: true,
  imports: [KENDO_TILELAYOUT, KENDO_BUTTONS, CommonModule],
})
export class MainComponent implements OnInit {
  private router = inject(Router);
  private breadcrumbService = inject(BreadcrumbService);
  private permService = inject(PermissionsService);

  // icons
  public svgCart: SVGIcon = cartIcon;
  public svgAnchor: SVGIcon = anchorIcon;
  public svgCode: SVGIcon = codeIcon;
  public svgMember: SVGIcon = userIcon;
  public svgRole: SVGIcon = userOutlineIcon;
  public svgFeature: SVGIcon = wrenchIcon;
  public svgRoleFeature: SVGIcon = slidersIcon;

  // responsive layout
  public useAccordion = signal(false);  // true for tablet + mobile (< 1024px)
  public accordionCols = signal(1);     // 1 = mobile, 2 = tablet

  @HostListener('window:resize')
  onResize(): void {
    this.updateLayout();
  }

  private updateLayout(): void {
    const w = window.innerWidth;
    this.useAccordion.set(w < 1024);
    this.accordionCols.set(w < 640 ? 1 : 2);
  }

  // root node (tree)
  public readonly root: MenuNode = menuyItems as MenuNode;

  // groups shown on tiles (root children)
  public groups: MenuNode[] = [];

  // UI layout metadata (only ulClass is still needed)
  private tileUi: Record<string, { ulClass?: string }> = {
    salesOrderProcessing: { ulClass: "two-columns" },
    inventoryManagement: { ulClass: "two-columns" }
  };

  // icon mapping by option textName (leaf)
  private optionIcons: Record<string, SVGIcon> = {
    // Access Control
    features: this.svgFeature,
    roles: this.svgRole,
    roleFeatureAssignments: this.svgRoleFeature,
    userRoleAssignments: this.svgRoleFeature,

    // Configurations
    warehouseList: this.svgCart,
    distributionCenterList: this.svgCart,
    itemcategory: this.svgAnchor,
    itemCreate: this.svgAnchor,
    adminItems: this.svgAnchor,

    // Members
    memberList: this.svgMember,
    salesOrdersExisting: this.svgCode,

    // Vendors
    vendorList: this.svgAnchor,
    vendorItems: this.svgCode,

    // Customers
    customerList: this.svgAnchor,
    customerItems: this.svgCart,
    planBenefits: this.svgAnchor,

    // Purchase Orders
    purchaseOrdersCreate: this.svgAnchor,
    purchaseOrdersExisting: this.svgCart,
    receiving: this.svgAnchor,

    // Sales Orders
    salesOrdersDeliveryCreate: this.svgAnchor,
    salesOrdersSplitCreate: this.svgCart,
    salesOrdersExisting1: this.svgCode,
    shipmentExisting: this.svgCart,
    pickingCreate: this.svgAnchor,
    pickingExisting: this.svgCart,
    packingCreate: this.svgAnchor,
    packingExisting: this.svgCart,

    // Inventory
    adjustmentWarehouseCreate: this.svgAnchor,
    adjustmentDCCreate: this.svgCart,
    adjustmentExisting: this.svgCode,
    transferCreate: this.svgAnchor,
    transferExisting: this.svgCart,
  };

  ngOnInit(): void {
    this.updateLayout();
    // root children only; no redundant map
    this.groups = (this.root.children ?? []).filter(
      (g) => (g.children?.length ?? 0) > 0
    );
  }

  public getGroupUlClass(groupTextName: string): string {
    return this.tileUi[groupTextName]?.ulClass ?? "";
  }

  public getIcon(optionTextName: string): SVGIcon {
    return this.optionIcons[optionTextName] ?? this.svgAnchor;
  }

  public hasPermission(optionTextName: string): boolean {
    const leaf = this.findNodeByTextName(this.root, optionTextName);
   
    return !!leaf?.path && this.permService.canRoute(leaf.path);
  }

  public hasGroupPermission(groupTextName: string): boolean {
    const group = (this.root.children ?? []).find(
      (g) => g.textName === groupTextName
    );
    if (!group) return false;

    // visible if any child leaf is permitted
    return (group.children ?? []).some((opt) => this.hasPermission(opt.textName));
  }

  public onClick(optionTextName: string): void {
    const leaf = this.findNodeByTextName(this.root, optionTextName);
    if (!leaf?.path) return;

    this.router.navigate([leaf.path]).catch(() => {
      alert(`Navigation to "${leaf.path}" failed. Route does not exist.`);
    });

    // breadcrumb: resolved by the service from the tree
    this.breadcrumbService.updateBreadcrumbFromTree(this.root, optionTextName);

    // title
    this.breadcrumbService.updatePageTitle(leaf.text);
  }

  // Only groups that have at least one permitted option
  public get visibleGroups(): MenuNode[] {
    return (this.groups ?? []).filter((g) =>
      this.hasGroupPermission(g.textName)
    );
  }

  // Max 3 columns, fill left-to-right, skipping hidden groups automatically
  public getDynamicCol(visibleIndex: number): number {
    return (visibleIndex % 3) + 1;
  }

  private findNodeByTextName(root: MenuNode, textName: string): MenuNode | null {
    const stack: MenuNode[] = [root];

    while (stack.length) {
      const node = stack.pop()!;
      if (node.textName === textName) return node;

      const children = node.children ?? [];
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push(children[i]);
      }
    }

    return null;
  }

  public getImageSrc(optionTextName: string): string {
    const leaf = this.findNodeByTextName(this.root, optionTextName);
    return leaf?.imageSrc ?? "assets/user.png"; // fallback
  }
}
